// Game state + cascade physics for Letter Flick.
//
// This module is framework- and DOM-free by design. It owns the mutable
// GameState (cell <-> token bidirectional maps + exited letters) and exposes
// two functions: `dispatchFlick` (UI-facing: takes a source cell + direction)
// and `applyFlick` (token-id-facing: the primitive operation, line-for-line
// port of scripts/letter_flick/solver.py:apply_flick_tokenmap).
//
// The cascade rules are inherited verbatim from the Python harness:
//   1. The flicker cannot move into an immediately-adjacent token.
//   2. There MUST be at least one token strictly beyond the flicker along
//      the flick direction (otherwise the flicker itself would exit, which
//      is forbidden).
//   3. The flicker walks one cell at a time. When it strikes another token,
//      it stops one cell back and the struck token inherits the motion.
//      Cascade continues until the moving token exits the board.
//
// Token-index semantics: tokens are stable integers assigned at puzzle
// start. A token's *current cell* changes as it moves, but its index never
// does. This is what lets Phase 3 implement undo by re-applying a recorded
// move sequence (or, equivalently, snapshotting + restoring the maps).

import type { GameEvent } from './events'
import type { Puzzle } from './puzzle'

// Board geometry. 7x7 per SPEC §3.1.
export const BOARD_SIZE = 7 as const

// Pointer-flick distance threshold in CSS pixels (SPEC §3.2).
export const FLICK_THRESHOLD_PX = 30 as const

// Maximum undos allowed per puzzle. The undo button disables once
// `state.undoCount` reaches this value, and a successful flick that fills the
// bar with the wrong word transitions the game to 'lost' (see controller).
//
// SPEC §3.6 recommended 5; raised to 6 after analyzing the iOS playtest
// undo-count distribution (see docs/undo-distribution-2026-05-17.html) — at
// cap = 6, ~85% of historical wins remain playable on the curator's data.
export const MAX_UNDOS = 6 as const

// Whether the game is still in progress, solved, or lost. Set by the
// controller after every successful flick (and reset to 'playing' on undo).
// The controller uses this to lock board input + disable the undo button.
export type GameStatus = 'playing' | 'won' | 'lost'

// Direction matches Python's IntEnum order (UP, DOWN, LEFT, RIGHT).
export type Direction = 'UP' | 'DOWN' | 'LEFT' | 'RIGHT'

// Integer encoding of Direction matching the iOS `GridDirection` mapping and
// the corpus's per-slot valid-choice entries. Kept in lockstep with the
// Direction union:
//   UP    -> 0
//   DOWN  -> 1
//   LEFT  -> 2
//   RIGHT -> 3
// Used by the per-slot bar provenance: the recorded direction is one half of
// the (sourceCell, direction) pair the solution-DAG walk (solutionGraph.ts)
// matches against the current node's outgoing edges to color each slot.
export function directionToInt(direction: Direction): number {
  switch (direction) {
    case 'UP':
      return 0
    case 'DOWN':
      return 1
    case 'LEFT':
      return 2
    case 'RIGHT':
      return 3
  }
}

// Per-slot record of HOW the player filled bar slot `i` — the source cell
// they touched and the direction they flicked it. Consumed by the solution-DAG
// walk (solutionGraph.ts): the (sourceCell, direction) pair is matched against
// the current node's outgoing edges to decide BLUE (an edge keeping a win
// reachable) vs ORANGE (right letter via an off-graph move).
//
// `sourceCell` is the encoded board cell of the flicked token AT FLICK TIME.
// For a flick of a token still in its initial position this equals
// `puzzle.initialLayout[tokenIdx]`; once a token has shifted via a prior
// cascade it equals its current cell. The DAG's edges carry the flick-time
// source cell too, so the comparison is direct and frame-consistent.
//
// Stored as a structural object via interface for clarity, NOT a
// readonly [number, number] tuple, because we mutate the parallel array via
// push/length=0 in the same pattern as `exitedLetters`. Tuple literals would
// require fresh allocations on every snapshot restore via `as const`, which
// the in-place mutation pattern can't express.
export interface ExitProvenance {
  readonly sourceCell: EncodedCell
  readonly direction: number
}

// Cells are encoded as row * BOARD_SIZE + col (range [0, 49)). Using a
// number lets us hash via plain Map<number, T> in O(1) without per-call
// object allocations — important for the cascade hot path.
export type EncodedCell = number

// Token indices: stable integers 0..N-1, where N = puzzle.initialLayout.length.
export type TokenIdx = number

export function encode(row: number, col: number): EncodedCell {
  return row * BOARD_SIZE + col
}

export function decode(cell: EncodedCell): readonly [number, number] {
  return [Math.floor(cell / BOARD_SIZE), cell % BOARD_SIZE] as const
}

function onBoard(row: number, col: number): boolean {
  return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE
}

function delta(direction: Direction): readonly [number, number] {
  switch (direction) {
    case 'UP':
      return [-1, 0]
    case 'DOWN':
      return [1, 0]
    case 'LEFT':
      return [0, -1]
    case 'RIGHT':
      return [0, 1]
  }
}

export interface GameState {
  readonly puzzleId: string
  readonly wordLength: number
  // Parallel array: letters[i] is the lowercase letter of token i. Indexed
  // (not a Map) because token indices are dense and never reused; the array
  // never shrinks even after a token exits the board (the letter persists in
  // `exitedLetters`). Reads must narrow `string | undefined` under
  // noUncheckedIndexedAccess — see `applyFlick` for the canonical pattern.
  readonly letters: readonly string[]
  // PRIMARY occupancy map: which token currently sits at which cell.
  // Mirrors Python's `token_map: dict[Cell, int]`.
  readonly cellToToken: Map<EncodedCell, TokenIdx>
  // SECONDARY index: the inverse of cellToToken. Maintained in lockstep so
  // dispatchFlick can find a token by its source cell without scanning.
  readonly tokenToCell: Map<TokenIdx, EncodedCell>
  // Letters in exit order. May grow beyond wordLength (matching the iOS
  // LetterFlickViewModel.swift commitSlide() behavior at line 172); the bar
  // renders only the first wordLength entries. Phase 3 win-detection ends
  // the game at exactly wordLength exits, making this overflow unreachable
  // in normal play once Phase 3 lands.
  readonly exitedLetters: string[]
  // PARALLEL to exitedLetters: per-slot record of WHICH source cell the
  // player flicked AND in which direction, for the flick that filled this
  // slot. exitedProvenance.length is always kept equal to exitedLetters.length
  // by applyFlick (one push each) and by the snapshot save/restore in undo.ts
  // (both arrays included in the snapshot). The solution-DAG walk feeds
  // exitedProvenance[i] to the per-node edge match for the blue-vs-orange
  // distinction (a matching outgoing edge from the current node, not equality).
  //
  // Mirrors the iOS app's "ExitProvenance" tracking — same naming, same
  // semantics.
  readonly exitedProvenance: ExitProvenance[]
  // MUTABLE: the controller writes to these after every flick + undo.
  // NOT marked readonly — they are owned/reassigned by the controller. The
  // surrounding GameState reference itself stays stable for the game's life.
  status: GameStatus
  undoCount: number
  // Incremented once per *successful* flick in applyFlick (NOT on illegal
  // flicks; NOT on undo, which decrements via snapshot restore). Counts the
  // total flicks the player has executed in the current session — used by
  // Phase 6 stats + the share grid's distribution. Persisted to localStorage.
  flickCount: number
  // APPEND-ONLY across the entire game session. `applyFlick` appends a
  // `{type:'flick', slotIndex, letter}` event on success; the controller's
  // onUndoClick appends `{type:'undo'}` after restoreSnapshot. Undo does NOT
  // touch this log — phantom (undone-away) flick events stay in the log so
  // the share grid generator can snapshot the bar's state at each undo
  // (see sharegrid.ts for the multi-row snapshot algorithm).
  //
  // Why `GameEvent[]` and not `readonly GameEvent[]`: same rationale as
  // exitedLetters above — the controller and applyFlick mutate via push().
  // The reference to the container is logically stable for the game's life;
  // the readonly modifier on the surrounding GameState fields applies to the
  // reference, not the contents.
  eventLog: GameEvent[]
}

export function initState(puzzle: Puzzle): GameState {
  const cellToToken = new Map<EncodedCell, TokenIdx>()
  const tokenToCell = new Map<TokenIdx, EncodedCell>()
  for (let i = 0; i < puzzle.initialLayout.length; i += 1) {
    const cell = puzzle.initialLayout[i]
    if (cell === undefined) {
      // Should be impossible given `i < length`, but TS doesn't know that.
      throw new Error(`Puzzle initialLayout missing index ${i}`)
    }
    cellToToken.set(cell, i)
    tokenToCell.set(i, cell)
  }
  return {
    puzzleId: puzzle.id,
    wordLength: puzzle.wordLength,
    letters: puzzle.letterByTokenIdx,
    cellToToken,
    tokenToCell,
    exitedLetters: [],
    exitedProvenance: [],
    status: 'playing',
    undoCount: 0,
    flickCount: 0,
    eventLog: [],
  }
}

// Phase 7 cascade-animation sentinel: a TrajectoryStep whose `toCell` is
// EXIT_CELL represents a token that left the board. The animator translates
// this into a translate of (cellSizePx * step.distance) past the source cell
// in the cascade direction, so the tile slides off the board edge before
// being clipped by the board's overflow: hidden.
//
// Chosen as -1 because EncodedCell is always non-negative (0..48 for a 7x7
// board), so the sentinel is unambiguous and a single `=== EXIT_CELL` check
// is sufficient narrowing. We do NOT widen EncodedCell to `number | -1`; the
// trajectory's `toCell: EncodedCell | ExitCell` is the only spot where the
// sentinel ever appears.
export const EXIT_CELL = -1 as const
export type ExitCell = typeof EXIT_CELL

// A single step in a cascade trajectory — one moving token's journey from its
// pre-flick cell to either its post-cascade resting cell or off the board.
//
// Recorded as a side-product of `applyFlick`'s existing cascade walk; the
// algorithm itself is unchanged from Phase 2. The animator consumes the
// trajectory sequentially to drive the on-screen cascade animation.
//
// `distance` is in CELLS (Manhattan): for non-exit steps, the number of grid
// cells the tile traverses from `fromCell` to `toCell`; for exit steps, the
// number of cells from `fromCell` to the conceptual cell just off the board
// edge (i.e. one more than `manhattan(fromCell, lastInBoundsCell)`). The
// animator multiplies `distance * MS_PER_CELL` to get the per-step duration.
//
// `letter` is denormalised from `state.letters[tokenIdx]` so the animator
// stays free of any direct GameState dependency (cleaner test boundary; the
// animator only ever needs the letter for potential future rendering needs).
export interface TrajectoryStep {
  readonly tokenIdx: TokenIdx
  readonly fromCell: EncodedCell
  readonly toCell: EncodedCell | ExitCell
  readonly direction: Direction
  /** Manhattan cells from fromCell to toCell. May be 0 for a pass-through
   *  cascade member (token whose motion transfers instantly because the next
   *  strike target is immediately adjacent). For exit steps, equals
   *  manhattan(fromCell, lastInBoundsCell) + 1 (the off-board step). */
  readonly distance: number
  readonly letter: string
}

export type DispatchResult =
  | {
      readonly ok: true
      readonly exitedLetter: string
      // Retained for Phase 3 undo: restoring an exit puts this token back at
      // the cell it occupied before the flick (the source cell of the flick).
      readonly exitedTokenIdx: TokenIdx
      // Phase 7: per-token cascade trajectory in moving-tile order. The first
      // step is always the flicker; the last step is always the exiting token
      // (the cascade only terminates on a board exit). Length >= 1.
      readonly trajectory: readonly TrajectoryStep[]
    }
  | { readonly ok: false; readonly reason: 'adjacent' | 'noTokenBeyond' | 'tokenNotFound' }

// Manhattan distance in cells between two encoded cells. Used by the cascade
// trajectory recorder to compute per-step animation durations. Pure / cheap;
// not exported because no other module needs it.
function manhattanCells(a: EncodedCell, b: EncodedCell): number {
  const [ar, ac] = decode(a)
  const [br, bc] = decode(b)
  return Math.abs(ar - br) + Math.abs(ac - bc)
}

// Phase 2: state.exitedLetters may grow beyond wordLength. The bar renders
// only the first wordLength entries; extras are tracked silently. Phase 3's
// win-detection ends the game at exactly wordLength exits with the correct
// outcome (win iff letters match target), so this overflow window is
// effectively unreachable in normal play once Phase 3 lands.
export function dispatchFlick(
  state: GameState,
  source: EncodedCell,
  direction: Direction,
): DispatchResult {
  const tokenIdx = state.cellToToken.get(source)
  if (tokenIdx === undefined) {
    return { ok: false, reason: 'tokenNotFound' }
  }
  return applyFlick(state, tokenIdx, direction)
}

// Line-for-line port of scripts/letter_flick/solver.py:apply_flick_tokenmap
// (lines 131-189). Mutates `state.cellToToken`, `state.tokenToCell`, and on
// success appends one letter to `state.exitedLetters`.
export function applyFlick(
  state: GameState,
  tokenIdx: TokenIdx,
  direction: Direction,
): DispatchResult {
  const flickerPos = state.tokenToCell.get(tokenIdx)
  if (flickerPos === undefined) {
    return { ok: false, reason: 'tokenNotFound' }
  }

  const [dr, dc] = delta(direction)
  const [fr, fc] = decode(flickerPos)

  // Rule 1: cannot flick into an immediately-adjacent token.
  const adjR = fr + dr
  const adjC = fc + dc
  if (onBoard(adjR, adjC) && state.cellToToken.has(encode(adjR, adjC))) {
    return { ok: false, reason: 'adjacent' }
  }

  // Rule 2: there must be a token strictly beyond the flicker along the
  // flick direction (otherwise the flicker itself would exit).
  if (!hasTokenBeyond(state.cellToToken, fr, fc, dr, dc)) {
    return { ok: false, reason: 'noTokenBeyond' }
  }

  // Cascade walk. Both maps are mutated in lockstep.
  //
  // Phase 7 addition: a trajectory[] is built as a SIDE-PRODUCT of the same
  // walk. The algorithm itself is unchanged; we just record each moving
  // token's (from, to) pair. A new step opens whenever motion transfers
  // (flicker start; struck-token cascade transfer) and closes when motion
  // either lands on a cell (cascade transfer) or exits the board.
  const trajectory: TrajectoryStep[] = []
  let movingIdx = tokenIdx
  // The cell at which the CURRENT moving token first began its motion. Updated
  // each time motion transfers to a new token (see the strike branch below).
  let stepFrom: EncodedCell = flickerPos
  state.cellToToken.delete(flickerPos)
  state.tokenToCell.delete(movingIdx)
  let cur = flickerPos

  // Helper closure: look up the moving token's letter without `!` assertions.
  // Throws on invariant violation (same behavior as the in-line check below).
  const letterFor = (idx: TokenIdx): string => {
    const lt = state.letters[idx]
    if (lt === undefined) {
      throw new Error(`Invariant: no letter for tokenIdx ${idx}`)
    }
    return lt
  }

  // Loop bound: each iteration either advances `cur` strictly toward the
  // edge (by one cell) or terminates. The board is 7x7, so this loop runs
  // at most BOARD_SIZE times before the moving token exits.
  while (true) {
    const [cr, cc] = decode(cur)
    const nr = cr + dr
    const nc = cc + dc

    if (!onBoard(nr, nc)) {
      // Moving token exits the board. Look up its letter via the parallel
      // letters array, narrowing the `string | undefined` indexing result
      // (noUncheckedIndexedAccess). We never use `!` non-null assertions.
      const exitedLetter = letterFor(movingIdx)
      // Close the current trajectory step: the moving token exits, so toCell
      // is EXIT_CELL and the distance includes the off-board step (cells
      // traversed = manhattan(from, cur) + 1).
      trajectory.push({
        tokenIdx: movingIdx,
        fromCell: stepFrom,
        toCell: EXIT_CELL,
        direction,
        distance: manhattanCells(stepFrom, cur) + 1,
        letter: exitedLetter,
      })
      // Counted only on the success branch (before the bar push, so a future
      // throw inside the push — there isn't one today, but defensively — can't
      // leave flickCount drifted ahead of exitedLetters).
      state.flickCount += 1
      state.exitedLetters.push(exitedLetter)
      // Bar provenance is kept length-parallel to exitedLetters. The "source"
      // is the CELL the player flicked from (`flickerPos`, captured at the
      // top of applyFlick before the cascade walk mutated the maps), NOT the
      // exiting token's final position. This matches the solution-DAG edge
      // semantics: each edge is keyed by a (source_tile, direction) pair, so the
      // walk can compare this provenance directly against the graph's edges.
      state.exitedProvenance.push({
        sourceCell: flickerPos,
        direction: directionToInt(direction),
      })
      // Event-log append. slotIndex is the 0-based bar slot this letter just
      // filled, derived AFTER the push. Note: in normal play the win check
      // halts input once exitedLetters.length reaches wordLength, so slotIndex
      // stays in [0, wordLength-1]. After an undo of the final slot, the
      // restored length is wordLength-1 and the next flick re-fills slot N-1.
      // The share-grid generator walks the log forward, using `letter` to
      // reconstruct the bar at each undo snapshot; slotIndex is kept for
      // self-description + future stats consumers.
      state.eventLog.push({
        type: 'flick',
        slotIndex: state.exitedLetters.length - 1,
        letter: exitedLetter,
      })
      return { ok: true, exitedLetter, exitedTokenIdx: movingIdx, trajectory }
    }

    const nextCell = encode(nr, nc)
    const struckIdx = state.cellToToken.get(nextCell)
    if (struckIdx !== undefined) {
      // Close the current step: the moving token lands at `cur` before the
      // strike. Distance is `manhattan(stepFrom, cur)`, which is 0 when the
      // struck token has no room before its own strike — that token is a
      // pass-through (its motion transfers instantly to the next struck
      // token), and the animator handles distance-0 as a no-op.
      trajectory.push({
        tokenIdx: movingIdx,
        fromCell: stepFrom,
        toCell: cur,
        direction,
        distance: manhattanCells(stepFrom, cur),
        letter: letterFor(movingIdx),
      })
      // Cascade transfer: moving token lands at `cur`; struck token now
      // carries the motion forward from `nextCell`.
      state.cellToToken.set(cur, movingIdx)
      state.tokenToCell.set(movingIdx, cur)
      state.cellToToken.delete(nextCell)
      state.tokenToCell.delete(struckIdx)
      movingIdx = struckIdx
      stepFrom = nextCell
      cur = nextCell
      continue
    }

    // Empty cell — keep moving.
    cur = nextCell
  }
}

function hasTokenBeyond(
  occupied: ReadonlyMap<EncodedCell, TokenIdx>,
  fromR: number,
  fromC: number,
  dr: number,
  dc: number,
): boolean {
  let r = fromR + dr
  let c = fromC + dc
  while (onBoard(r, c)) {
    if (occupied.has(encode(r, c))) return true
    r += dr
    c += dc
  }
  return false
}

// Pure predicate: does this token have any legal flick in this direction
// right now? Checks the two preconditions from `applyFlick` (no adjacent
// token in flick direction; at least one token strictly beyond) without
// mutating state. Used by `hasAnyLegalFlick` for stuck-state detection.
function isFlickLegal(state: GameState, tokenIdx: TokenIdx, direction: Direction): boolean {
  const flickerPos = state.tokenToCell.get(tokenIdx)
  if (flickerPos === undefined) return false
  const [dr, dc] = delta(direction)
  const [fr, fc] = decode(flickerPos)
  const adjR = fr + dr
  const adjC = fc + dc
  if (onBoard(adjR, adjC) && state.cellToToken.has(encode(adjR, adjC))) return false
  if (!hasTokenBeyond(state.cellToToken, fr, fc, dr, dc)) return false
  return true
}

// Pure predicate: is at least one (token, direction) pair a legal flick from
// the current board? Short-circuits on first legal pair. Worst case for a
// fully-occupied 7x7 board: 49 tokens x 4 directions = 196 cheap checks.
// Used by the controller to detect stuck states for lose-condition handling.
export function hasAnyLegalFlick(state: GameState): boolean {
  for (const tokenIdx of state.tokenToCell.keys()) {
    if (isFlickLegal(state, tokenIdx, 'UP')) return true
    if (isFlickLegal(state, tokenIdx, 'DOWN')) return true
    if (isFlickLegal(state, tokenIdx, 'LEFT')) return true
    if (isFlickLegal(state, tokenIdx, 'RIGHT')) return true
  }
  return false
}
